import type { TaskListItem, Stage } from '@cg-projects/shared';

export type GroupBy = 'stage' | 'assignee' | 'priority' | 'due';
export type SortBy = 'priority' | 'due' | 'name' | 'manual';

export interface TaskGroup {
  key: string;
  label: string;
  count: number;
  tasks: TaskListItem[];
  fold: boolean; // start collapsed (folded stages)
}

export type DateField = 'film_date' | 'date_deadline';

// Which date a task is "due" on for the given area.
export function taskDate(t: TaskListItem, dateField: DateField): string | null {
  return dateField === 'film_date' ? t.filmDate ?? t.deadline : t.deadline ?? t.filmDate;
}

function midnight(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function parseDate(iso: string | null): Date | null {
  if (!iso) return null;
  // Handle both "YYYY-MM-DD" and Odoo datetimes "YYYY-MM-DD HH:MM:SS".
  const hasTime = iso.includes('T') || iso.includes(' ');
  const d = new Date(hasTime ? iso.replace(' ', 'T') : `${iso}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : midnight(d);
}

export type DueBucket = 'overdue' | 'today' | 'week' | 'later' | 'none';

export function dueBucket(iso: string | null, now = new Date()): DueBucket {
  const d = parseDate(iso);
  if (!d) return 'none';
  const today = midnight(now);
  const diffDays = Math.round((d.getTime() - today.getTime()) / 86_400_000);
  if (diffDays < 0) return 'overdue';
  if (diffDays === 0) return 'today';
  if (diffDays <= 7) return 'week';
  return 'later';
}

const DUE_META: Record<DueBucket, { label: string; order: number }> = {
  overdue: { label: 'Overdue', order: 0 },
  today: { label: 'Today', order: 1 },
  week: { label: 'This week', order: 2 },
  later: { label: 'Later', order: 3 },
  none: { label: 'No date', order: 4 },
};

// ---- Sorting ----
export function sortTasks(tasks: TaskListItem[], sortBy: SortBy, dateField: DateField): TaskListItem[] {
  const arr = [...tasks];
  const byDue = (a: TaskListItem, b: TaskListItem) => {
    const da = parseDate(taskDate(a, dateField))?.getTime() ?? Infinity;
    const db = parseDate(taskDate(b, dateField))?.getTime() ?? Infinity;
    return da - db;
  };
  switch (sortBy) {
    case 'priority':
      return arr.sort((a, b) => (b.priority === '1' ? 1 : 0) - (a.priority === '1' ? 1 : 0) || byDue(a, b));
    case 'due':
      return arr.sort(byDue);
    case 'name':
      return arr.sort((a, b) => a.name.localeCompare(b.name));
    case 'manual':
    default:
      return arr.sort((a, b) => a.sequence - b.sequence || a.id - b.id);
  }
}

// ---- Grouping ----
export function groupTasks(
  tasks: TaskListItem[],
  groupBy: GroupBy,
  ctx: { stages: Stage[]; dateField: DateField },
): TaskGroup[] {
  if (groupBy === 'stage') return groupByStage(tasks, ctx.stages);
  if (groupBy === 'assignee') return groupByAssignee(tasks);
  if (groupBy === 'priority') return groupByPriority(tasks);
  return groupByDue(tasks, ctx.dateField);
}

function groupByStage(tasks: TaskListItem[], stages: Stage[]): TaskGroup[] {
  const byStage = new Map<number, TaskListItem[]>();
  for (const t of tasks) {
    const arr = byStage.get(t.stageId);
    if (arr) arr.push(t);
    else byStage.set(t.stageId, [t]);
  }
  const groups: TaskGroup[] = stages.map((s) => ({
    key: `stage:${s.id}`,
    label: s.name,
    fold: s.fold,
    tasks: byStage.get(s.id) ?? [],
    count: (byStage.get(s.id) ?? []).length,
  }));
  const known = new Set(stages.map((s) => s.id));
  const orphan = tasks.filter((t) => !known.has(t.stageId));
  if (orphan.length) groups.push({ key: 'stage:other', label: 'Other', fold: false, tasks: orphan, count: orphan.length });
  return groups;
}

function groupByAssignee(tasks: TaskListItem[]): TaskGroup[] {
  const groups = new Map<number, { label: string; tasks: TaskListItem[] }>();
  const unassigned: TaskListItem[] = [];
  for (const t of tasks) {
    if (t.assignees.length === 0) {
      unassigned.push(t);
      continue;
    }
    for (const a of t.assignees) {
      const g = groups.get(a.id) ?? { label: a.name, tasks: [] };
      g.tasks.push(t);
      groups.set(a.id, g);
    }
  }
  const out: TaskGroup[] = [...groups.entries()]
    .map(([id, g]) => ({ key: `assignee:${id}`, label: g.label, fold: false, tasks: g.tasks, count: g.tasks.length }))
    .sort((a, b) => a.label.localeCompare(b.label));
  if (unassigned.length)
    out.push({ key: 'assignee:none', label: 'Unassigned', fold: false, tasks: unassigned, count: unassigned.length });
  return out;
}

function groupByPriority(tasks: TaskListItem[]): TaskGroup[] {
  const high = tasks.filter((t) => t.priority === '1');
  const normal = tasks.filter((t) => t.priority !== '1');
  const out: TaskGroup[] = [];
  if (high.length) out.push({ key: 'prio:1', label: 'High priority', fold: false, tasks: high, count: high.length });
  out.push({ key: 'prio:0', label: 'Normal', fold: false, tasks: normal, count: normal.length });
  return out;
}

// ---- Adding into a group ----
/** What a task typed under a group must carry so it lands IN that group
 *  instead of jumping to another one (or out of sight). null = this group has
 *  no honest meaning for a new task (the orphan "Other" stage, Overdue, a
 *  week-or-later bucket), so it offers no add line. */
export interface GroupCreateDefaults {
  stageId?: number;
  assigneeIds?: number[];
  priority?: '1';
  filmDate?: string;
  deadline?: string;
}

function isoDay(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function createDefaultsFor(
  groupKey: string,
  ctx: { stages: Stage[]; dateField: DateField },
  now = new Date(),
): GroupCreateDefaults | null {
  const [kind, raw = ''] = groupKey.split(':');
  if (kind === 'stage') {
    const id = Number(raw);
    return raw !== '' && Number.isInteger(id) && ctx.stages.some((s) => s.id === id) ? { stageId: id } : null;
  }
  // Every other grouping starts the task in the project's first stage — the
  // same place the calendar's day "+" puts one.
  const first = ctx.stages[0]?.id;
  if (first === undefined) return null;
  if (kind === 'assignee') {
    if (raw === 'none') return { stageId: first };
    const id = Number(raw);
    return Number.isInteger(id) && id > 0 ? { stageId: first, assigneeIds: [id] } : null;
  }
  if (kind === 'prio') return raw === '1' ? { stageId: first, priority: '1' } : { stageId: first };
  if (kind === 'due') {
    if (raw === 'none') return { stageId: first };
    if (raw === 'today') {
      const day = isoDay(now);
      return ctx.dateField === 'film_date' ? { stageId: first, filmDate: day } : { stageId: first, deadline: day };
    }
    return null;
  }
  return null;
}

function groupByDue(tasks: TaskListItem[], dateField: DateField): TaskGroup[] {
  const buckets = new Map<DueBucket, TaskListItem[]>();
  for (const t of tasks) {
    const b = dueBucket(taskDate(t, dateField));
    const arr = buckets.get(b);
    if (arr) arr.push(t);
    else buckets.set(b, [t]);
  }
  return [...buckets.entries()]
    .sort((a, b) => DUE_META[a[0]].order - DUE_META[b[0]].order)
    .map(([b, list]) => ({ key: `due:${b}`, label: DUE_META[b].label, fold: b === 'none' || b === 'later', tasks: list, count: list.length }));
}
